/*
 * Copyright (c) Vaisala Oyj. All rights reserved.
 */

////////////////////////////////////////////////////
// RESULT VALIDITY
////////////////////////////////////////////////////

export type ValidityState = {
  severity: ValiditySeverity;
  reason?: ValidityReason;
};
type ValiditySeverity = 'off' | 'near' | 'on';
type ValidityReason = 'condensation' | 'condensationNear' | 'zeroDuration' | 'temperatureHigh' | 'pressureHigh' | 'frostForming';

export function getValidityState(reason?: ValidityReason): ValidityState {
  if (!reason) {
    return { severity: 'off' };
  }
  switch (reason) {
    case 'condensationNear':
    case 'temperatureHigh':
    case 'pressureHigh':
    case 'frostForming':
      return { severity: 'near', reason: reason };

    case 'condensation':
    case 'zeroDuration':
      return { severity: 'on', reason: reason };
  }
}

////////////////////////////////////////////////////
// PARAMETERS
////////////////////////////////////////////////////

// public types
export type ParameterModifier = number | boolean;

export type ParameterName =
  | keyof PreconditioningCardData
  | keyof ConditioningCardData
  | keyof DwellCardData
  | keyof AerationCardData
  | keyof OpenSystemCalculationCardData
  | keyof FreeEvaporationCalculationCardData;

////////////////////////////////////////////////////
// RESULT CLASSES
////////////////////////////////////////////////////

export type MethodType = 'closedSystem' | 'openSystem' | 'freeEvaporation';

export class CalculatorResult {
  public validityState: ValidityState = getValidityState();
  constructor(public method: MethodType, public calculatorData: CalculatorDataBase, public graphData: GraphDataBase) {}
}

export class ClosedSystemCalculatorResult extends CalculatorResult {
  constructor() {
    super('closedSystem', new ClosedSystemCalculatorData(), new ClosedSystemGraphData());
  }
}
export class OpenSystemCalculatorResult extends CalculatorResult {
  constructor() {
    super('openSystem', new OpenSystemCalculatorData(), new OpenSystemGraphData());
  }
}

export class FreeEvaporationCalculatorResult extends CalculatorResult {
  constructor() {
    super('freeEvaporation', new FreeEvaporationCalculatorData(), new FreeEvaporationGraphData());
  }
}

////////////////////////////////////////////////////
// METHOD PHASE DEFINITIONS
////////////////////////////////////////////////////

class CalculatorDataBase {
  constructor(public cards: CardDataBase[]) {
    // if (new.target === CalculatorDataBase) {
    //   throw new TypeError("Abstract class: Cannot construct CalculatorDataBase instances directly");
    // }
  }
}

export class ClosedSystemCalculatorData extends CalculatorDataBase {
  constructor() {
    super([new PreconditioningCardData(), new ConditioningCardData(), new DwellCardData(), new AerationCardData()]);
  }

  // convenience getters
  public get preconditioning() {
    return this.cards[0] as PreconditioningCardData;
  }
  public get conditioning() {
    return this.cards[1] as ConditioningCardData;
  }
  public get dwell() {
    return this.cards[2] as DwellCardData;
  }
  public get aeration() {
    return this.cards[3] as AerationCardData;
  }
}

export class OpenSystemCalculatorData extends CalculatorDataBase {
  constructor() {
    super([new OpenSystemCalculationCardData()]);
  }

  // convenience getters
  public get calculation() {
    return this.cards[0] as OpenSystemCalculationCardData;
  }
}

export class FreeEvaporationCalculatorData extends CalculatorDataBase {
  constructor() {
    super([new FreeEvaporationCalculationCardData()]);
  }

  // convenience getters
  public get calculation() {
    return this.cards[0] as FreeEvaporationCalculationCardData;
  }
}

////////////////////////////////////////////////////
// CARD CONTENTS
////////////////////////////////////////////////////

export class CardDataBase {}

export class PreconditioningCardData extends CardDataBase {
  public dehumidification = false;
  public volume = 0.0;
  public duration = 0.0;

  // FROM
  public relativeHumidity0 = 0.0;
  public temperature0 = 0.0;
  public pressure0 = 0.0;

  // TO
  public relativeHumidity1 = 0.0;
  public temperature1 = 0.0;
  public pressure1 = 0.0;

  // OUT
  public dryingRate = 0.0;
  //relativeHumidity1
  public relativeSaturation1 = 0.0;
  public h2o2Concentration1 = 0.0;
}

class ConditioningCardData extends CardDataBase {
  // IN
  public duration = 0.0;
  public injectionRate = 0.0;
  public liquidConcentration = 0.0;
  public evaporatorEfficiency = 0.0;
  public decompositionRate = 0.0;

  // OUT
  public relativeHumidity2 = 0.0;
  public relativeSaturation2 = 0.0;
  public h2o2Concentration2 = 0.0;
  public mixtureDewPoint2 = 0.0;
}

class DwellCardData extends CardDataBase {
  // IN
  public duration = 0.0;
  public injectionRate = 0.0;

  // OUT
  public relativeHumidity3 = 0.0;
  public relativeSaturation3 = 0.0;
  public h2o2Concentration3 = 0.0;
  public mixtureDewPoint3 = 0.0;
}

class AerationCardData extends CardDataBase {
  // IN
  public duration = 0.0;
  public ventilationRate = 0.0;
  public freshAirRelativeHumidity = 0.0;
  public freshAirTemperature = 0.0;
  public catalysatorEfficiency = 0.0;

  // OUT
  public relativeHumidity4 = 0.0;
  public relativeSaturation4 = 0.0;
  public h2o2Concentration4 = 0.0;
}

class OpenSystemCalculationCardData extends CardDataBase {
  // CARRIER GAS INLET
  public relativeHumidity0 = 0.0;
  public flowRate0 = 0.0;

  // LIQUID H2O2
  public h2o2Concentration = 0.0;
  public flowRate1 = 0.0;

  // OUTLET
  public temperature = 0.0;
  public pressure = 0.0;
  // OUT
  public relativeHumidity1 = 0.0;
  public relativeSaturation = 0.0;
  public VH2O2Concentration = 0.0;
}

class FreeEvaporationCalculationCardData extends CardDataBase {
  // INPUTS
  public temperature = 0.0;
  public pressure = 0.0;
  public liquidH2O2Concentration = 0.0;

  // OUTPUTS
  public relativeHumidity = 0.0;
  public relativeSaturation = 0.0;
  public h2o2Concentration = 0.0;
}

////////////////////////////////////////////////////
// GRAPH DATA
////////////////////////////////////////////////////

class GraphDataBase {}

// TODO: define graph data structure
class ClosedSystemGraphData extends GraphDataBase {}

// TODO: define graph data structure
class OpenSystemGraphData extends GraphDataBase {}

// No data required as there's currently no graph related to free evaporation method,
// but this class is defined for the sake of consistency and possible future needs.
class FreeEvaporationGraphData extends GraphDataBase {}
